<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import SettingsPageLayout from 'dashboard/components-next/captain/pageComponents/assistant/settings/SettingsPageLayout.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();

const carregando = ref(true);
const erro = ref(null);
const dados = ref(null);
const conversasPorMes = ref(300);

const moeda = valor =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: valor < 1 ? 4 : 2,
    maximumFractionDigits: valor < 1 ? 4 : 2,
  }).format(valor || 0);

const numero = valor => new Intl.NumberFormat('pt-BR').format(valor || 0);

const media = computed(() => dados.value?.media_por_conversa || {});
const total = computed(() => dados.value?.total || {});

const cartoes = computed(() => [
  {
    chave: 'custo',
    rotulo: t('CAPTAIN.ASSISTANTS.CONSUMO.CARDS.COST'),
    valor: moeda(total.value.custo),
  },
  {
    chave: 'conversas',
    rotulo: t('CAPTAIN.ASSISTANTS.CONSUMO.CARDS.CONVERSATIONS'),
    valor: numero(dados.value?.conversas_atendidas),
  },
  {
    chave: 'tokens',
    rotulo: t('CAPTAIN.ASSISTANTS.CONSUMO.CARDS.TOKENS'),
    valor: numero(total.value.tokens),
  },
  {
    chave: 'media',
    rotulo: t('CAPTAIN.ASSISTANTS.CONSUMO.CARDS.AVERAGE'),
    valor: moeda(media.value.custo),
  },
]);

const estimativa = computed(() => {
  const porConversa = media.value.custo || 0;
  const quantidade = Number(conversasPorMes.value) || 0;
  return {
    mes: porConversa * quantidade,
    ano: porConversa * quantidade * 12,
    tokens:
      ((media.value.tokens_entrada || 0) + (media.value.tokens_saida || 0)) *
      quantidade,
  };
});

const origens = computed(() => {
  const mapa = dados.value?.por_origem || {};
  return Object.entries(mapa).map(([chave, valores]) => ({
    chave,
    rotulo: t(`CAPTAIN.ASSISTANTS.CONSUMO.SOURCES.${chave.toUpperCase()}`),
    ...valores,
  }));
});

const carregar = async () => {
  carregando.value = true;
  erro.value = null;
  try {
    const { data } = await CaptainAssistantAPI.getConsumo();
    dados.value = data;
  } catch (e) {
    erro.value = t('CAPTAIN.ASSISTANTS.CONSUMO.ERROR');
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);
</script>

<template>
  <SettingsPageLayout
    :heading="t('CAPTAIN.ASSISTANTS.CONSUMO.TITLE')"
    :description="t('CAPTAIN.ASSISTANTS.CONSUMO.DESCRIPTION')"
  >
    <div v-if="carregando" class="text-sm text-n-slate-11">
      {{ t('CAPTAIN.ASSISTANTS.CONSUMO.LOADING') }}
    </div>

    <div v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</div>

    <div v-else class="flex flex-col gap-8">
      <section class="flex flex-col gap-3">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.CONSUMO.THIS_MONTH') }}
        </h3>
        <div class="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div
            v-for="cartao in cartoes"
            :key="cartao.chave"
            class="flex flex-col gap-1 p-4 border rounded-lg border-n-weak bg-n-solid-1"
          >
            <span class="text-xs text-n-slate-11">{{ cartao.rotulo }}</span>
            <span class="text-xl font-semibold tabular-nums text-n-slate-12">
              {{ cartao.valor }}
            </span>
          </div>
        </div>
        <p v-if="!dados.medido" class="text-xs text-n-amber-11">
          {{ t('CAPTAIN.ASSISTANTS.CONSUMO.NOT_MEASURED') }}
        </p>
      </section>

      <section class="flex flex-col gap-3">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.CONSUMO.CALCULATOR.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('CAPTAIN.ASSISTANTS.CONSUMO.CALCULATOR.HELP') }}
        </p>
        <div
          class="flex flex-col gap-4 p-4 border rounded-lg border-n-weak bg-n-solid-1 md:flex-row md:items-end"
        >
          <div class="flex flex-col gap-1 md:w-56">
            <label for="consumo-conversas" class="text-xs text-n-slate-11">{{
              t('CAPTAIN.ASSISTANTS.CONSUMO.CALCULATOR.INPUT')
            }}</label>
            <Input
              id="consumo-conversas"
              v-model="conversasPorMes"
              type="number"
              min="0"
            />
          </div>
          <div class="flex flex-1 gap-6">
            <div class="flex flex-col gap-1">
              <span class="text-xs text-n-slate-11">
                {{ t('CAPTAIN.ASSISTANTS.CONSUMO.CALCULATOR.PER_MONTH') }}
              </span>
              <span class="text-xl font-semibold tabular-nums text-n-teal-11">{{
                moeda(estimativa.mes)
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-xs text-n-slate-11">
                {{ t('CAPTAIN.ASSISTANTS.CONSUMO.CALCULATOR.PER_YEAR') }}
              </span>
              <span
                class="text-xl font-semibold tabular-nums text-n-slate-12"
                >{{ moeda(estimativa.ano) }}</span
              >
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-xs text-n-slate-11">
                {{ t('CAPTAIN.ASSISTANTS.CONSUMO.CALCULATOR.TOKENS') }}
              </span>
              <span
                class="text-xl font-semibold tabular-nums text-n-slate-12"
                >{{ numero(estimativa.tokens) }}</span
              >
            </div>
          </div>
        </div>
      </section>

      <section v-if="origens.length" class="flex flex-col gap-3">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.CONSUMO.BY_SOURCE') }}
        </h3>
        <div class="overflow-x-auto border rounded-lg border-n-weak">
          <table class="w-full text-sm">
            <thead class="bg-n-solid-2">
              <tr class="text-xs text-left text-n-slate-11">
                <th class="px-4 py-2 font-medium">
                  {{ t('CAPTAIN.ASSISTANTS.CONSUMO.TABLE.SOURCE') }}
                </th>
                <th class="px-4 py-2 font-medium text-right">
                  {{ t('CAPTAIN.ASSISTANTS.CONSUMO.TABLE.TURNS') }}
                </th>
                <th class="px-4 py-2 font-medium text-right">
                  {{ t('CAPTAIN.ASSISTANTS.CONSUMO.TABLE.TOKENS') }}
                </th>
                <th class="px-4 py-2 font-medium text-right">
                  {{ t('CAPTAIN.ASSISTANTS.CONSUMO.TABLE.COST') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="origem in origens"
                :key="origem.chave"
                class="border-t border-n-weak"
              >
                <td class="px-4 py-2 text-n-slate-12">{{ origem.rotulo }}</td>
                <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
                  {{ numero(origem.turnos) }}
                </td>
                <td class="px-4 py-2 text-right tabular-nums text-n-slate-11">
                  {{ numero(origem.tokens) }}
                </td>
                <td
                  class="px-4 py-2 font-medium text-right tabular-nums text-n-slate-12"
                >
                  {{ moeda(origem.custo) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="flex flex-col gap-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.CONSUMO.PRICES.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{
            t('CAPTAIN.ASSISTANTS.CONSUMO.PRICES.HELP', {
              dolar: moeda(dados.precos.dolar),
            })
          }}
        </p>
      </section>
    </div>
  </SettingsPageLayout>
</template>
